import { configureStore, combineReducers, createListenerMiddleware } from '@reduxjs/toolkit';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import type { PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer from '../features/auth/slice';
import menuReducer from '../features/menu/menuSlice';
import mediaResolverReducer from '../features/media/mediaResolverSlice';
import { menuApi } from '../features/menu';
import { gridApi } from '../features/grid';
import { companyBankAccountApi } from '../features/grid/api/companyBankAccountApi';
import { subscriptionProofBankImportApi } from '../features/grid/api/subscriptionProofBankImportApi';
import { invoiceDeliveryApi } from '../features/grid/api/invoiceDeliveryApi';
import { financeEmailApi } from '../features/grid/api/financeEmailApi';
import { simpleOutboundApi } from '../features/grid/api/simpleOutboundApi';
import { financeEmailHistoryActionApi } from '../features/grid/api/financeEmailHistoryActionApi';
import { wiseApi } from '../features/grid/api/wiseApi';
import { summaryApi } from '../features/summary';
import { gridStateApi } from '../features/grid/api/gridStateApi';
import { sensitiveDataApi } from '../features/sensitive-data/api/sensitiveDataApi';
import type { AuthState } from '../features/auth/types';
import { notificationsApi } from '../features/notifications';
import { dashboardApi } from '../features/dashboard/api/dashboardApi';
import { authApi } from '../features/auth/api/authApi.ts';
import { fileStorageApi } from '../features/file-storage';
import { mediaProbeApi } from '../features/media/api/mediaProbeApi';
import { commentsApi } from '../features/comments/api/commentsApi';
import { getRolesFromToken } from '../services/tokenService';
import { colleagueFeedbackApi } from '../features/colleagueRating/api/colleagueFeedbackApi.ts';
import { momSalaryReportApi } from '../features/momSalaryReport';
import { momSalesReportApi } from '../features/momSalesReport';
import { revenueReportApi } from '../features/revenueReport';
import { userManagementApi } from '../features/grid/api/userManagementApi';
import { employeeReviewApi } from '../features/employee-review/api/employeeReviewApi';
import { aiImplementationApi } from '../features/ai-implementation/api/aiImplementationApi';
import { bankStatementManagementImportApi } from '../features/bank-statement-import/api/bankStatementManagementImportApi';
import { bankAccountBalanceReportApi } from '../features/bankAccountBalanceReport';
import { expenseCategoryReportApi } from '../features/expenseCategoryReport';
import { financeEmailInboxApi } from '../features/finance-email-inbox';
import { logout } from '../features/auth/slice';

const apiSlices = [
  authApi,
  menuApi,
  gridApi,
  companyBankAccountApi,
  subscriptionProofBankImportApi,
  invoiceDeliveryApi,
  financeEmailApi,
  simpleOutboundApi,
  financeEmailHistoryActionApi,
  wiseApi,
  gridStateApi,
  summaryApi,
  notificationsApi,
  sensitiveDataApi,
  dashboardApi,
  fileStorageApi,
  mediaProbeApi,
  commentsApi,
  colleagueFeedbackApi,
  momSalaryReportApi,
  momSalesReportApi,
  revenueReportApi,
  userManagementApi,
  employeeReviewApi,
  aiImplementationApi,
  bankStatementManagementImportApi,
  bankAccountBalanceReportApi,
  expenseCategoryReportApi,
  financeEmailInboxApi,
];

const cacheResetMiddleware = createListenerMiddleware();

const resetApiCaches = (dispatch: (action: { type: string }) => unknown) => {
  apiSlices.forEach((api) => {
    dispatch(api.util.resetApiState());
  });
};

cacheResetMiddleware.startListening({
  actionCreator: logout,
  effect: async (_action, listenerApi) => {
    // Ensure the new token is persisted before queries re-run and prepareHeaders reads from localStorage.
    if (typeof localStorage !== 'undefined') {
      for (let i = 0; i < 20 && !localStorage.getItem('access_token'); i++) {
        await listenerApi.delay(10);
      }
    }

    resetApiCaches(listenerApi.dispatch);
  },
});

cacheResetMiddleware.startListening({
  matcher: authApi.endpoints.login.matchFulfilled,
  effect: async (_action, listenerApi) => {
    resetApiCaches(listenerApi.dispatch);
  },
});

// Transform to validate token expiry on rehydration
const tokenExpiryTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: AuthState) => inboundState,

  // Transform state being rehydrated
  (outboundState: AuthState) => {
    // Check if token exists and is expired
    if (outboundState.accessToken) {
      const tokenExpiry = localStorage.getItem('token_expiry');
      if (tokenExpiry) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (currentTime > parseInt(tokenExpiry, 10)) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('token_expiry');
          return {
            ...outboundState,
            accessToken: null,
            roles: [],
          };
        }
      }
    }

    if (!outboundState.roles?.length && outboundState.accessToken) {
      return {
        ...outboundState,
        roles: getRolesFromToken(outboundState.accessToken),
      };
    }

    return {
      ...outboundState,
      roles: outboundState.roles ?? [],
    };
  },

  { whitelist: ['auth'] } // Only apply to auth slice
);

// Combine reducers
const rootReducer = combineReducers({
  auth: authReducer,
  mediaResolver: mediaResolverReducer,
  [authApi.reducerPath]: authApi.reducer,
  menu: menuReducer,
  [menuApi.reducerPath]: menuApi.reducer,
  [gridApi.reducerPath]: gridApi.reducer,
  [companyBankAccountApi.reducerPath]: companyBankAccountApi.reducer,
  [subscriptionProofBankImportApi.reducerPath]: subscriptionProofBankImportApi.reducer,
  [invoiceDeliveryApi.reducerPath]: invoiceDeliveryApi.reducer,
  [financeEmailApi.reducerPath]: financeEmailApi.reducer,
  [simpleOutboundApi.reducerPath]: simpleOutboundApi.reducer,
  [financeEmailHistoryActionApi.reducerPath]: financeEmailHistoryActionApi.reducer,
  [wiseApi.reducerPath]: wiseApi.reducer,
  [gridStateApi.reducerPath]: gridStateApi.reducer,
  [summaryApi.reducerPath]: summaryApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [sensitiveDataApi.reducerPath]: sensitiveDataApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [fileStorageApi.reducerPath]: fileStorageApi.reducer,
  [mediaProbeApi.reducerPath]: mediaProbeApi.reducer,
  [commentsApi.reducerPath]: commentsApi.reducer,
  [colleagueFeedbackApi.reducerPath]: colleagueFeedbackApi.reducer,
  [momSalaryReportApi.reducerPath]: momSalaryReportApi.reducer,
  [momSalesReportApi.reducerPath]: momSalesReportApi.reducer,
  [revenueReportApi.reducerPath]: revenueReportApi.reducer,
  [userManagementApi.reducerPath]: userManagementApi.reducer,
  [employeeReviewApi.reducerPath]: employeeReviewApi.reducer,
  [aiImplementationApi.reducerPath]: aiImplementationApi.reducer,
  [bankStatementManagementImportApi.reducerPath]: bankStatementManagementImportApi.reducer,
  [bankAccountBalanceReportApi.reducerPath]: bankAccountBalanceReportApi.reducer,
  [expenseCategoryReportApi.reducerPath]: expenseCategoryReportApi.reducer,
  [financeEmailInboxApi.reducerPath]: financeEmailInboxApi.reducer,
});

// Type for the root state before persistence
export type RootState = ReturnType<typeof rootReducer>;

// Persist configuration
const persistConfig: PersistConfig<RootState> = {
  key: 'root',
  storage,
  whitelist: ['auth'], // Only persist auth slice
  transforms: [tokenExpiryTransform],
};

// Create persisted reducer with type assertion
// Note: This bypasses TypeScript's type checking but is safe because:
// 1. PersistGate ensures rehydration completes before app renders
// 2. We only persist 'auth' slice which has proper initial state
// 3. The tokenExpiryTransform ensures auth state is always valid
const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          // fileStorageApi.viewFile intentionally returns a raw Blob for PDF/image preview.
          // Narrowed to this api slice's own action types only (not app-wide).
          `${fileStorageApi.reducerPath}/executeQuery/fulfilled`,
        ],
        ignoredActionPaths: [
          'meta.arg.originalArgs',
          'meta.baseQueryMeta.request',
          'meta.baseQueryMeta.response',
        ],
        ignoredPaths: [
          'auth',
          'menu',
          summaryApi.reducerPath,
          // viewFile intentionally caches a raw Blob for PDF/image preview —
          // it's never persisted or sent over the wire again, so it's safe to ignore here.
          new RegExp(`^${fileStorageApi.reducerPath}\\.queries\\.viewFile`),
        ],
      },
    })
      .prepend(cacheResetMiddleware.middleware)
      // Add RTK Query middleware for caching and request lifecycle
      .concat(authApi.middleware)
      .concat(menuApi.middleware)
      .concat(gridApi.middleware)
      .concat(companyBankAccountApi.middleware)
      .concat(subscriptionProofBankImportApi.middleware)
      .concat(invoiceDeliveryApi.middleware)
      .concat(financeEmailApi.middleware)
      .concat(simpleOutboundApi.middleware)
      .concat(financeEmailHistoryActionApi.middleware)
      .concat(wiseApi.middleware)
      .concat(gridStateApi.middleware)
      .concat(summaryApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(sensitiveDataApi.middleware)
      .concat(dashboardApi.middleware)
      .concat(fileStorageApi.middleware)
      .concat(mediaProbeApi.middleware)
      .concat(commentsApi.middleware)
      .concat(colleagueFeedbackApi.middleware)
      .concat(momSalaryReportApi.middleware)
      .concat(momSalesReportApi.middleware)
      .concat(revenueReportApi.middleware)
      .concat(userManagementApi.middleware)
      .concat(employeeReviewApi.middleware)
      .concat(aiImplementationApi.middleware)
      .concat(bankStatementManagementImportApi.middleware)
      .concat(bankAccountBalanceReportApi.middleware)
      .concat(expenseCategoryReportApi.middleware)
      .concat(financeEmailInboxApi.middleware),
});

// Create persistor
export const persistor = persistStore(store);

// Infer the `AppDispatch` type from the store
export type AppDispatch = typeof store.dispatch;

// Export pre-typed hooks for usage throughout the app
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
